import { runInAction } from "mobx";

import { logger } from "@/modules/logger";
import { CustomEventName, dispatchCustomEvent } from "@/modules/custom-event";
import {
  RecallPermissionKind,
  RecallPermissionMap,
  RecallPermissionStatus,
} from "@/modules/electron/types";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";

export class DeviceManager {
  constructor(private readonly store: AppStoreAudioCaptureStore) {}

  setShowPermissionModal(show: boolean) {
    runInAction(() => {
      this.store.showPermissionModal = show;
    });
  }

  setRecallPermissionStatus(status: RecallPermissionMap) {
    const normalized = status ? { ...status } : {};
    runInAction(() => {
      this.store.recallPermissionStatus = normalized;
      this.store.hasSystemAudioRecordingPermission =
        normalized?.[RecallPermissionKind.ScreenCapture] === RecallPermissionStatus.Granted;
      this.store.hasMicrophoneRecordingPermission =
        normalized?.[RecallPermissionKind.Microphone] === RecallPermissionStatus.Granted;
      this.store.hasAccessibilityPermission =
        normalized?.[RecallPermissionKind.Accessibility] === RecallPermissionStatus.Granted;
    });
  }

  async syncRecallPermissions(status: RecallPermissionMap) {
    logger.info({
      message: "[AppStoreAudioCaptureStore] syncRecallPermissions",
      info: { status },
    });

    this.setRecallPermissionStatus(status ?? {});

    dispatchCustomEvent(CustomEventName.AppPermissions, {
      permissions: {
        systemAudio: this.store.hasSystemAudioRecordingPermission,
        microphone: this.store.hasMicrophoneRecordingPermission,
      },
    });

    const missingPermission =
      !this.store.hasMicrophoneRecordingPermission ||
      !this.store.hasSystemAudioRecordingPermission ||
      !this.store.hasAccessibilityPermission;

    if (!missingPermission && this.store.showPermissionModal) {
      this.setShowPermissionModal(false);
    }
  }

  async detectAudioInputDevices(stream: MediaStream | null) {
    if (!stream) return;

    const devices = await navigator.mediaDevices.enumerateDevices();

    const enabledTracks = stream.getAudioTracks().filter(track => track.enabled && !track.muted);
    const activeDevices = enabledTracks
      .map(track => devices.find(device => device.deviceId === track.getSettings().deviceId))
      .filter(Boolean) as MediaDeviceInfo[];
    const activeAudioInputDeviceLabel = activeDevices[0]?.label ?? null;

    const activeAudioInputDevices = activeDevices.map(device => ({
      deviceId: device.deviceId,
      groupId: device.groupId,
    }));
    const rawDeviceInfo = activeDevices.map(device => device.toJSON());

    logger.info({
      message: "[AppStoreAudioCaptureStore] Track media stream",
      info: {
        activeAudioInputDevices,
        friendlyDebugInfo: rawDeviceInfo,
        activeAudioInputDeviceLabel,
      },
    });

    runInAction(() => {
      this.store.activeAudioInputDevices = activeAudioInputDevices;
      this.store.activeAudioInputDeviceLabel = activeAudioInputDeviceLabel;
      this.store.updateFriendlyDebugInfo({
        activeAudioInputDevices,
        activeAudioInputDeviceLabel,
        rawDeviceInfo,
      });
    });
  }

  startDesktopInitializationBuffer(callback: () => void) {
    if (!this.store.isDesktop) {
      callback();
      return;
    }

    this.clearDesktopInitializationBuffer();

    const bufferMs = this.store.DESKTOP_INITIALIZATION_BUFFER.as("milliseconds");
    runInAction(() => {
      this.store.desktopInitializationBufferUntil = Date.now() + bufferMs;
    });

    this.store.desktopInitializationBufferTimer = setTimeout(() => {
      this.store.desktopInitializationBufferTimer = null;
      runInAction(() => {
        this.store.desktopInitializationBufferUntil = null;
      });
      callback();
    }, bufferMs);
  }

  clearDesktopInitializationBuffer() {
    if (this.store.desktopInitializationBufferTimer) {
      clearTimeout(this.store.desktopInitializationBufferTimer);
      this.store.desktopInitializationBufferTimer = null;
    }

    runInAction(() => {
      this.store.desktopInitializationBufferUntil = null;
    });
  }

  getMissingDesktopPermission(): boolean {
    if (!this.store.isDesktop) return false;
    return (
      !this.store.hasMicrophoneRecordingPermission ||
      !this.store.hasSystemAudioRecordingPermission ||
      !this.store.hasAccessibilityPermission
    );
  }
}
